import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import WhatsAppButton from '../components/WhatsAppButton';

function Pricing() {
  return (
    <div className="min-h-screen bg-white">
      {/* Hero Section */}
      <section className="bg-gradient-to-br from-primary-600 to-secondary-600 text-white py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <h1 className="text-4xl md:text-5xl font-bold mb-6">
              Simple, Transparent Pricing
            </h1>
            <p className="text-xl mb-8 max-w-3xl mx-auto">
              Choose the perfect model for your business. Start at RM 39 per photobook with volume discounts and revenue sharing opportunities.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Pricing Models */}
      <section className="py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="section-title">Choose Your Business Model</h2>
            <p className="section-subtitle">Flexible pricing options designed to grow with your business</p>
          </div>
          
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 max-w-6xl mx-auto">
            {/* Pre-Purchase Model */}
            <motion.div 
              className="bg-white rounded-2xl shadow-xl p-8 border-t-4 border-primary-500 relative overflow-hidden"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.1 }}
            >
              <div className="absolute -top-1 -right-1 bg-primary-500 text-white text-sm font-bold px-4 py-2 rounded-bl-lg">
                MOST POPULAR
              </div>
              
              <div className="text-center mb-8">
                <h3 className="text-2xl font-bold mb-4">Pre-Purchase Model</h3>
                <div className="mb-4">
                  <span className="text-4xl font-bold text-primary-600">RM 39</span>
                  <span className="text-gray-600 ml-2">starting price per photobook</span>
                </div>
                <p className="text-gray-600">Lower prices with higher quantity purchases</p>
              </div>

              <div className="space-y-4 mb-8">
                <div className="flex items-start">
                  <svg className="h-5 w-5 text-primary-500 mr-3 mt-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                  </svg>
                  <div>
                    <span className="font-semibold">Volume Discounts:</span>
                    <p className="text-sm text-gray-600 mt-1">The more you buy, the less you pay per unit</p>
                  </div>
                </div>
                
                <div className="flex items-start">
                  <svg className="h-5 w-5 text-primary-500 mr-3 mt-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                  </svg>
                  <div>
                    <span className="font-semibold">Design Services Included:</span>
                    <p className="text-sm text-gray-600 mt-1">Custom branded templates designed for your business</p>
                  </div>
                </div>
                
                <div className="flex items-start">
                  <svg className="h-5 w-5 text-primary-500 mr-3 mt-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                  </svg>
                  <div>
                    <span className="font-semibold">Free Malaysia Shipping:</span>
                    <p className="text-sm text-gray-600 mt-1">Delivery included within Malaysia</p>
                  </div>
                </div>
                
                <div className="flex items-start">
                  <svg className="h-5 w-5 text-primary-500 mr-3 mt-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                  </svg>
                  <div>
                    <span className="font-semibold">Custom Landing Page:</span>
                    <p className="text-sm text-gray-600 mt-1">Branded hosting for your photobook experience</p>
                  </div>
                </div>
              </div>

              <Link to="/contact" className="btn-primary w-full text-center block">
                Get Volume Pricing Quote
              </Link>
            </motion.div>

            {/* Revenue Share Model */}
            <motion.div 
              className="bg-white rounded-2xl shadow-xl p-8 border-t-4 border-secondary-500"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.2 }}
            >
              <div className="text-center mb-8">
                <h3 className="text-2xl font-bold mb-4">Revenue Share Model</h3>
                <div className="mb-4">
                  <span className="text-4xl font-bold text-secondary-600">Up to 25%</span>
                  <span className="text-gray-600 ml-2">revenue share</span>
                </div>
                <p className="text-gray-600">Earn from every customer purchase</p>
              </div>

              <div className="space-y-4 mb-8">
                <div className="flex items-start">
                  <svg className="h-5 w-5 text-secondary-500 mr-3 mt-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                  </svg>
                  <div>
                    <span className="font-semibold">No Upfront Investment:</span>
                    <p className="text-sm text-gray-600 mt-1">Start earning without initial costs</p>
                  </div>
                </div>
                
                <div className="flex items-start">
                  <svg className="h-5 w-5 text-secondary-500 mr-3 mt-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                  </svg>
                  <div>
                    <span className="font-semibold">Design Services Included:</span>
                    <p className="text-sm text-gray-600 mt-1">Custom branded templates designed for your business</p>
                  </div>
                </div>
                
                <div className="flex items-start">
                  <svg className="h-5 w-5 text-secondary-500 mr-3 mt-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                  </svg>
                  <div>
                    <span className="font-semibold">Malaysia Shipping Included:</span>
                    <p className="text-sm text-gray-600 mt-1">Free delivery within Malaysia for customers</p>
                  </div>
                </div>
                
                <div className="flex items-start">
                  <svg className="h-5 w-5 text-secondary-500 mr-3 mt-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                  </svg>
                  <div>
                    <span className="font-semibold">Custom Landing Page:</span>
                    <p className="text-sm text-gray-600 mt-1">Branded hosting for your photobook experience</p>
                  </div>
                </div>
              </div>

              <Link to="/contact" className="btn-secondary w-full text-center block">
                Start Revenue Sharing
              </Link>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Pricing Details */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="section-title">What's Included</h2>
            <p className="section-subtitle">Everything you need to get started</p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            <div className="text-center">
              <div className="bg-primary-100 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
                <span className="text-primary-600 text-2xl">🎨</span>
              </div>
              <h3 className="text-lg font-semibold mb-2">Design Services</h3>
              <p className="text-gray-600 text-sm">Custom branded templates tailored to your business</p>
            </div>
            
            <div className="text-center">
              <div className="bg-primary-100 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
                <span className="text-primary-600 text-2xl">🚚</span>
              </div>
              <h3 className="text-lg font-semibold mb-2">Malaysia Shipping</h3>
              <p className="text-gray-600 text-sm">Free delivery within Malaysia included</p>
            </div>
            
            <div className="text-center">
              <div className="bg-primary-100 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
                <span className="text-primary-600 text-2xl">🌐</span>
              </div>
              <h3 className="text-lg font-semibold mb-2">Custom Landing Page</h3>
              <p className="text-gray-600 text-sm">Branded hosting for your photobook experience</p>
            </div>
            
            <div className="text-center">
              <div className="bg-primary-100 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
                <span className="text-primary-600 text-2xl">📞</span>
              </div>
              <h3 className="text-lg font-semibold mb-2">Support</h3>
              <p className="text-gray-600 text-sm">Dedicated support throughout your journey</p>
            </div>
          </div>
        </div>
      </section>

      {/* Shipping Notice */}
      <section className="py-16 bg-gray-100">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-6">
            <div className="flex items-center justify-center mb-4">
              <svg className="h-6 w-6 text-yellow-600 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <h3 className="text-lg font-semibold text-yellow-800">International Shipping</h3>
            </div>
            <p className="text-yellow-700">
              Worldwide shipping is available but will be charged separately to customers. 
              Malaysia shipping is included in all pricing plans.
            </p>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-r from-primary-600 to-secondary-600 text-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl font-bold mb-6">Ready to Get Started?</h2>
          <p className="text-xl mb-8">
            Contact us today to discuss which pricing model works best for your business.
          </p>
          <Link to="/contact" className="bg-white text-primary-600 px-8 py-4 rounded-lg font-semibold hover:bg-gray-100 transition-colors inline-block">
            Get Your Custom Quote
          </Link>
        </div>
        <WhatsAppButton />
      </section>
    </div>
  );
}

export default Pricing;